<template>
    <div class="custom-breadcrumb">
        <div id="statPanelSlider" style="height: 30px; display: flex; align-items: center" @click="toggle()">
            <Arrows id="arrows" :class="{ open: !largeHeader }" data-v-step="1" style="margin-right: 5px"></Arrows>
        </div>
        <ul>
            <li>
                <div class="item" @click="chatMainMenu('/')">
                    {{ $t("message.home") }}
                </div>
            </li>
            <li v-if="paths">
                <div
                    v-for="(item, index) in paths"
                    :key="'br-' + index"
                    class="item sec"
                    @click="item.path === 'chat' ? chatMainMenu(item.path) : go(item.path)"
                >
                    {{ item.title }}
                </div>
            </li>
            <li v-if="showSubjects">
                <select id="departSelect" v-model="selectedSubjectModel">
                    <option value="null">{{ $t("message.allDepartments") }}</option>
                    <option v-for="subject in subjects" :key="subject._id" :value="subject._id">
                        {{ subject.languageContent[$i18n.locale].name }}
                    </option>
                </select>
            </li>
            <li v-if="select" class="d-flex align-items-end">
                <slot></slot>
            </li>

            <li class="ml-auto">
                <div class="d-flex justify-content-between align-items-center">
                    <div
                        v-if="showTrialRemainDay && trialInfo.trial && settings && settings.plan"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="$t('billing.trialTimeInformation', { dayCount: trialInfo.remainingDays })"
                        class="item four mr-2"
                        style="
                            overflow: hidden;
                            text-overflow: ellipsis;
                            display: -webkit-box;
                            -webkit-box-orient: vertical;
                            -webkit-line-clamp: 1;
                            word-break: break-all;
                        "
                    >
                        <span class="text-info bold" @click="$router.push('/billing')">
                            {{ $t("billing.trialTimeInformationShort", { dayCount: trialInfo.remainingDays }) }}
                        </span>
                    </div>
                    <button
                        v-if="customerDomainsFetched && !isSiteIntegrated()"
                        @click.prevent="integrationInstruction"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="$t('message.notIntegratedDesc')"
                        class="actionButtonBreadcrumb"
                    >
                        <md-icon style="color: red">support</md-icon>
                        {{ $t("message.notIntegrated") }}
                    </button>
                </div>
            </li>
            <li v-if="largeHeader">
                <md-button
                    style="min-width: 20px; max-width: 25px"
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                        theme: isDarkMode ? 'light-border' : 'google',
                    }"
                    :content="socketConnected ? $t('message.connectServer') : $t('message.disconnectServer')"
                    class="actionButtonBreadcrumb m-0 w-100"
                    @click="disconnectSocket"
                >
                    <div :class="{ blinkAnimation: !socketConnected }">
                        <md-icon
                            :style="{
                                color: socketConnected ? 'green' : 'red',
                            }"
                            >wifi
                        </md-icon>
                    </div>
                </md-button>
            </li>
            <li v-if="largeHeader && whatsappEnabled">
                <md-button
                    style="min-width: 20px; max-width: 25px"
                    class="actionButtonBreadcrumb m-0"
                    @click="$router.push('/integrations/whatsapp')"
                >
                    <md-icon
                        :style="{ color: !wpInfo.initialized ? 'gray' : whatsappServiceIsHealthy ? 'green' : 'red' }"
                        >whatsapp</md-icon
                    >
                </md-button>
            </li>
            <li v-if="largeHeader">
                <md-button
                    style="min-width: 20px; max-width: 25px"
                    v-tippy="{
                        followCursor: true,
                        placement: 'bottom-end',
                        theme: isDarkMode ? 'light-border' : 'google',
                    }"
                    :content="offlineMode ? $t('message.liveChatOff') : $t('message.liveChatOn')"
                    class="actionButtonBreadcrumb m-0"
                >
                    <md-icon :style="{ color: offlineMode ? 'red' : 'green' }">support_agent</md-icon>
                </md-button>
            </li>
            <li v-if="largeHeader" class="item four">
                <span class="date">{{ $t("time.today") }}:</span><span class="time">{{ getToday() }}</span>
            </li>
        </ul>
        <HeaderRight v-if="!largeHeader" />
    </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import Arrows from "../svg/Arrows";
import HeaderRight from "../HeaderRight";

export default {
    name: "AppBreadCrumb",
    components: {
        Arrows,
        HeaderRight,
    },
    data() {
        return {
            customerDomainsFetched: false,
            wpInterval: null,
        };
    },
    props: {
        paths: {
            type: Array,
            required: false,
        },
        select: {
            type: Boolean,
            required: false,
        },
        showSubjects: {
            type: Boolean,
            required: false,
            default: false,
        },
        showTrialRemainDay: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            wpInfo: (state) => state.whatsapp.info,
            largeHeader: (state) => state.app.largeHeader,
            settings: (state) => state.setting.settings,
            customerDomains: (state) => state.visitor.customerDomains,
            site: (state) => state.site.site,
            socketConnected: (state) => state.socketStatus.socket_connected,
            debugMode: (state) => state.app.debugMode,
            socket: (state) => state.socketStatus.current_socket,
            offlineMode: (state) => state.setting.offlineMode,
            activeConversation: (state) =>
                state.app.fakeMode ? state.chat.fakeActiveConversation : state.chat.activeConversation,
            selectedSubject: (state) => state.subject.selectedSubject,
            trialInfo: (state) => {
                const site = state.site.site;
                if (site) {
                    const { createdAt } = site;
                    const tookDays = Math.max(moment().diff(moment(createdAt, "YYYY-MM-DD"), "day"), 0);
                    const isTrial = tookDays < 30;
                    return { trial: isTrial, remainingDays: isTrial ? 30 - tookDays : 0 };
                } else {
                    return { trial: false, remainingDays: 0 };
                }
            },
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            subjects: "subject/getUserSubjects",
            whatsappEnabled: "setting/whatsappEnabled",
            whatsappServiceIsHealthy: "whatsapp/isHealthy",
        }),
        selectedSubjectModel: {
            get() {
                return this.selectedSubject;
            },
            set(value) {
                this.updateSelectedSubject(value);
            },
        },
    },
    methods: {
        ...mapActions({
            getCustomerDomains: "visitor/getCustomerDomains",
            hideConversation: "chat/hideConversation",
            getSubjects: "subject/getSubjects",
            updateSelectedSubject: "subject/updateSelectedSubject",
            toggleLargeHeader: "app/toggleLargeHeader",
        }),
        integrationInstruction() {
            this.$router.push("install-code");
        },
        toggle() {
            this.toggleLargeHeader();
        },
        go(path) {
            if (path) {
                this.$router.push(path);
            }
        },
        getToday() {
            return moment().format("Do MMM");
        },
        chatMainMenu(path) {
            if (path) {
                this.$router.push(path);
                this.$emit("to-chat-main", path);
                this.hideConversation(this.activeConversation);
            }
        },
        disconnectSocket() {
            if (this.debugMode && this.socketConnected) {
                this.socket.disconnect();
            }
        },
        isSiteIntegrated() {
            return this.customerDomainsFetched && this.customerDomains.length;
        },
    },
    mounted() {
        this.getCustomerDomains().then((x) => {
            this.customerDomainsFetched = true;
        });
        if (this.showSubjects) this.getSubjects({ perPage: 1000, page: 1 });
    },
    beforeDestroy() {
        if (this.wpInterval) clearInterval(this.wpInterval);
    },
};
</script>

<style lang="scss" scoped>
#departSelect {
    border: none;
    background-color: #f3f6f9;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-family: Poppins;
    font-weight: bolder;
    color: #3699ff;
}

.new-theme {
    .blinkAnimation {
        i {
            animation: blinkAnim 1s infinite;
        }
    }

    @keyframes blinkAnim {
        from {
            color: #f90000;
            opacity: 0;
        }
        to {
            color: #ff0a0a;
            opacity: 1;
        }
    }

    .custom-breadcrumb {
        padding: 0 30px;
        background-color: #ffffff;
        min-height: 39px;
        max-height: 47px;
        display: flex;
        align-items: center;
        min-width: 1230px;

        ul {
            list-style: none;
            display: flex;
            margin: 0;
            padding: 0;
            width: 100%;

            li {
                border-right: 1px solid #ebedf3;
                margin-right: 3px;
                padding-right: 3px;

                &:last-child {
                    border-right: none;
                }
            }
        }

        .item {
            background-color: #fff4de;
            color: #ffa800;
            font-family: Poppins;
            font-weight: bolder;
            font-size: 12px;
            cursor: pointer;
            padding: 3px 10px;
            border-radius: 6px;

            &.sec {
                background-color: #c9f7f5;
                color: #1bc5bd;
            }

            &.third {
                background-color: #f3f6f9;
                color: #3699ff;
            }

            &.four {
                background-color: #f3f6f9;

                span.date {
                    color: #b5b5c3;
                    font-family: Poppins;
                    font-weight: 500;
                    font-size: 13px;
                }

                span.time {
                    color: #3699ff;
                    font-family: Poppins;
                    font-weight: 600;
                    font-size: 13px;
                    padding-left: 10px;
                }
            }
        }
    }
}
#arrows {
    cursor: pointer;
    transition: all 0.3s;
    transform: rotateZ(90deg);

    &.open {
        transform: rotateZ(270deg);
    }
}
</style>
